import React, { useEffect, useState, useCallback, ReactElement, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
    Box,
    CircularProgress,
    Alert,
  Card,
  CardContent,
    Grid,
    Select, MenuItem, FormControl, InputLabel,
    Collapse,
    IconButton,
    Divider,
    Stack,
    AlertTitle,
} from '@mui/material';
import { supabase } from '../lib/supabase';
import { Link as RouterLink } from 'react-router-dom';
import {
    SimCard as SimCardIcon,
    AccountBalanceWallet as AccountBalanceWalletIcon,
    Business as BusinessIcon,
    GroupWork as GroupWorkIcon,
    MoneyOff as MoneyOffIcon,
    PendingActions as PendingActionsIcon,
    Refresh as RefreshIcon,
    AddCircleOutline as AddCircleOutlineIcon,
    Warning as WarningIcon,
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    TrendingUp as TrendingUpIcon,
    AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';

import { ThresholdMonitoringDashboard } from '../components/ThresholdMonitoringDashboard';

// Add new counts to metrics
type DashboardMetrics = {
    totalOrganizations?: number;
    totalSims: number;
    activeSims: number;
    inactiveSims: number;
    totalWalletBalance?: number;
    pendingRequestCount?: number;
    orgsWithFeesDueCount?: number;
    orgsWithLowBalanceCount?: number;
    totalClusters: number;
    organizationName?: string;
    walletBalance?: number;
    isLowBalance?: boolean;
    hasFeesDue?: boolean;
};

// Types for chart data
type UsageDataPoint = { name: string; usage: number; }; // Combined usage (MB or R)
type StatusDataPoint = { name: string; value: number };
// type TopConsumer = { id: string; name: string; usage: number; };
type HistoricalCountDataPoint = { date: string; orgs: number; clusters: number; activeSims: number; }; // Added for Business Health Chart

// Types for user profile
type UserProfile = {
  id: string;
    role: 'superadmin' | 'admin' | 'user';
    organization_id: string | null;
    organization_name?: string; // Optional: Fetch org name if admin
};

// Simple type for sim data used in metrics calculation
type SimCardStatus = { id: string; status: string };
// Simple type for org data used in metrics calculation
type OrgDataForMetrics = { id: string; name: string; wallet_balance: number | null; low_balance_threshold: number | null; management_fee_due: number };

// Utility Functions
const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) return '-';
    return `R${amount.toFixed(2)}`;
};
const formatDataUsage = (mb: number | null | undefined): string => {
    if (mb === null || mb === undefined || isNaN(mb)) return '- MB';
    if (mb >= 1024) {
        return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(1)} MB`;
};
const formatAxisDate = (dateStr: string | number | Date): string => {
    try {
        const date = new Date(dateStr);
        return `${date.getDate()} ${date.toLocaleDateString('en-US', { month: 'short' })}`;
    } catch { return String(dateStr); }
}
const formatAxisMonth = (dateStr: string | number | Date): string => {
     try {
        const date = new Date(dateStr);
        return `${date.toLocaleDateString('en-US', { month: 'short' })} '${String(date.getFullYear()).slice(-2)}`;
    } catch { return String(dateStr); }
}

// Chart Components
// const DailyUsageChart = ({ data, title }: { data: UsageDataPoint[]; title?: string }) => (
//     <Paper sx={{ p: 2, height: 300, display: 'flex', flexDirection: 'column' }}>
//         <Typography variant="h6" gutterBottom>{title || 'Daily Usage'}</Typography>
//         <Box sx={{ flexGrow: 1 }}>
//             <ResponsiveContainer width="100%" height="100%">
//                 <BarChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
//                     <CartesianGrid strokeDasharray="3 3" vertical={false} />
//                     <XAxis dataKey="name" fontSize={10} tickFormatter={formatAxisDate} interval="preserveStartEnd" />
//                     <YAxis fontSize={10} unit=" MB" allowDecimals={false}/>
//                     <Tooltip formatter={(value: number) => [`${value.toFixed(1)} MB`, 'Usage']} />
//                     <Bar dataKey="usage" fill="#8884d8" barSize={10} />
//                 </BarChart>
//             </ResponsiveContainer>
//         </Box>
//     </Paper>
// );

// const MonthlyUsageChart = ({ data, title }: { data: UsageDataPoint[]; title?: string }) => (
//      <Paper sx={{ p: 2, height: 300, display: 'flex', flexDirection: 'column' }}>
//         <Typography variant="h6" gutterBottom>{title || 'Monthly Usage'}</Typography>
//          <Box sx={{ flexGrow: 1 }}>
//             <ResponsiveContainer width="100%" height="100%">
//                 <BarChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
//                     <CartesianGrid strokeDasharray="3 3" vertical={false} />
//                     <XAxis dataKey="name" fontSize={10} tickFormatter={formatAxisMonth} interval="preserveStartEnd" />
//                     <YAxis fontSize={10} unit=" MB" allowDecimals={false} />
//                      <Tooltip formatter={(value: number) => [formatDataUsage(value), 'Usage']} />
//                     <Bar dataKey="usage" fill="#82ca9d" />
//                 </BarChart>
//             </ResponsiveContainer>
//          </Box>
//     </Paper>
// );

// const SimStatusDonut = ({ data }: { data: StatusDataPoint[] }) => {
//     const COLORS = ['#4CAF50', '#F44336']; // Green for Active, Red for Inactive
//     // const total = data.reduce((sum, entry) => sum + entry.value, 0);
//     const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, value }: any) => {
//       const RADIAN = Math.PI / 180;
//       const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
//       const x = cx + radius * Math.cos(-midAngle * RADIAN);
//       const y = cy + radius * Math.sin(-midAngle * RADIAN);
//        if (percent < 0.05 || value === 0) return null;
//       return (
//         <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={12}>
//           {`${value} (${(percent * 100).toFixed(0)}%)`}
//         </text>
//       );
//   };

//   return (
//         <Paper sx={{ p: 2, height: 300, display: 'flex', flexDirection: 'column' }}>
//             <Typography variant="h6" gutterBottom>SIM Statuses</Typography>
//             <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', width: '100%' }}>
//                  <Box sx={{ width: '65%', height: '100%' }}>
//                     <ResponsiveContainer width="100%" height="100%">
//                         <PieChart>
//                             <Pie
//                                 data={data}
//                                 cx="50%"
//                                 cy="50%"
//                                 labelLine={false}
//                                 label={renderCustomizedLabel}
//                                 innerRadius={50}
//                                 outerRadius={80}
//                                 fill="#8884d8"
//                                 paddingAngle={2}
//                                 dataKey="value"
//                             >
//                                 {data.map((entry, index) => (
//                                     <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
//                                 ))}
//                             </Pie>
//                              <Tooltip formatter={(value: number, name: string) => [value, name]}/>
//                         </PieChart>
//                     </ResponsiveContainer>
//           </Box>
//                  <Box sx={{ width: '35%', pl: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
//                     {data.map((entry, index) => (
//                         <Box key={entry.name} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
//                             <Box sx={{ width: 12, height: 12, bgcolor: COLORS[index % COLORS.length], borderRadius: '50%', mr: 1 }} />
//                             <Typography variant="body2">{`${entry.name}: ${entry.value}`}</Typography>
//           </Box>
//                     ))}
//           </Box>
//           </Box>
//         </Paper>
//     );
// };

// --- Business Health Chart (New) ---
// const BusinessHealthChart = ({ data }: { data: HistoricalCountDataPoint[] }) => (
//     <Paper sx={{ p: 2, height: 300, display: 'flex', flexDirection: 'column' }}>
//         <Typography variant="h6" gutterBottom>Business Health Trends (Last 12 Months)</Typography>
//         <Box sx={{ flexGrow: 1 }}>
//             <ResponsiveContainer width="100%" height="100%">
//                 <LineChart data={data} margin={{ top: 5, right: 30, left: -20, bottom: 5 }}>
//                     <CartesianGrid strokeDasharray="3 3" vertical={false} />
//                     <XAxis dataKey="date" fontSize={10} tickFormatter={formatAxisMonth} interval="preserveStartEnd" />
//                     <YAxis fontSize={10} allowDecimals={false} />
//                     <Tooltip formatter={(value: number, name: string) => [value, name]} />
//                     <Legend />
//                     <Line type="monotone" dataKey="orgs" stroke="#8884d8" strokeWidth={2} name="Organizations" />
//                     <Line type="monotone" dataKey="clusters" stroke="#82ca9d" strokeWidth={2} name="Clusters" />
//                     <Line type="monotone" dataKey="activeSims" stroke="#ffc658" strokeWidth={2} name="Active SIMs" />
//                 </LineChart>
//             </ResponsiveContainer>
//         </Box>
//     </Paper>
// );

// Alert Banner Component
const AlertBanner = ({ 
  severity, 
  title, 
  message, 
  action, 
  onDismiss 
}: { 
  severity: 'error' | 'warning' | 'info';
  title: string;
  message: string;
  action?: ReactElement;
  onDismiss?: () => void;
}) => (
  <Alert 
    severity={severity} 
    sx={{ mb: 2 }}
    action={
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {action}
        {onDismiss && (
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={onDismiss}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        )}
      </Box>
    }
  >
    <AlertTitle>{title}</AlertTitle>
    {message}
  </Alert>
);

// Enhanced Financial Summary Component
const FinancialSummaryCard = ({ 
  walletBalance, 
  estimatedCost, 
  monthlyFee, 
  requiredBalance, 
  fundingGap,
  isLowBalance,
  onAddFunds 
}: {
  walletBalance: number;
  estimatedCost: number;
  monthlyFee: number;
  requiredBalance: number;
  fundingGap: number;
  isLowBalance: boolean;
  onAddFunds: () => void;
}) => {
  const hasGap = fundingGap > 0;
  
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <AttachMoneyIcon sx={{ mr: 1 }} />
          Financial Overview
        </Typography>
        
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">Current Wallet Balance</Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: isLowBalance ? 'warning.main' : 'success.main',
                fontWeight: 'bold'
              }}
            >
              {formatCurrency(walletBalance)}
            </Typography>
          </Box>
          
          <Divider />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">Estimated Top-up Cost</Typography>
            <Typography variant="body1">{formatCurrency(estimatedCost)}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">Monthly Management Fee</Typography>
            <Typography variant="body1">{formatCurrency(monthlyFee)}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">Required Balance</Typography>
            <Typography variant="body1">{formatCurrency(requiredBalance)}</Typography>
          </Box>
          
          <Divider />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">Funding Status</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {hasGap ? (
                <>
                  <WarningIcon sx={{ color: 'error.main', fontSize: 20 }} />
                  <Typography variant="body1" color="error.main" fontWeight="bold">
                    Gap: {formatCurrency(fundingGap)}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body1" color="success.main" fontWeight="bold">
                    ✓ Sufficient
                  </Typography>
                </>
              )}
            </Box>
          </Box>
          
          {hasGap && (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={onAddFunds}
              startIcon={<AddCircleOutlineIcon />}
            >
              Add {formatCurrency(Math.ceil(fundingGap / 10) * 10)} to Wallet
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

// Enhanced Metric Card with better visual hierarchy
const EnhancedStatCard = ({ 
  title, 
  value, 
  icon, 
  color, 
  isLoading, 
  subtitle,
  trend,
  onClick 
}: { 
  title: string;
  value: string | number;
  icon: ReactElement;
  color?: string;
  isLoading?: boolean;
  subtitle?: string;
  trend?: 'up' | 'down' | 'stable';
  onClick?: () => void;
}) => (
  <Card 
    sx={{ 
      height: '100%', 
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.2s ease-in-out',
      '&:hover': onClick ? {
        transform: 'translateY(-2px)',
        boxShadow: 4,
      } : {}
    }}
    onClick={onClick}
  >
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
          {title}
        </Typography>
        <Box sx={{ 
          p: 1, 
          borderRadius: 1, 
          bgcolor: color ? `${color}15` : 'primary.light',
          color: color || 'primary.main'
        }}>
          {icon}
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Typography 
              variant="h4" 
              sx={{ 
                color: color || 'text.primary',
                fontWeight: 'bold',
                lineHeight: 1
              }}
            >
              {value}
            </Typography>
            {trend && (
              <TrendingUpIcon 
                sx={{ 
                  fontSize: 20,
                  color: trend === 'up' ? 'success.main' : trend === 'down' ? 'error.main' : 'text.secondary',
                  transform: trend === 'down' ? 'rotate(180deg)' : 'none'
                }} 
              />
            )}
          </>
        )}
      </Box>
      
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

// Compact Chart Container
const CompactChartContainer = ({ 
  title, 
  children, 
  isCollapsible = false,
  defaultExpanded = true 
}: { 
  title: string;
  children: React.ReactNode;
  isCollapsible?: boolean;
  defaultExpanded?: boolean;
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <Box 
        sx={{ 
          p: 2, 
          pb: isCollapsible ? 1 : 2,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          cursor: isCollapsible ? 'pointer' : 'default'
        }}
        onClick={isCollapsible ? () => setExpanded(!expanded) : undefined}
      >
        <Typography variant="h6">{title}</Typography>
        {isCollapsible && (
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Box>
      
      {isCollapsible ? (
        <Collapse in={expanded}>
          <Box sx={{ height: 250, p: 2, pt: 0 }}>
            {children}
          </Box>
        </Collapse>
      ) : (
        <Box sx={{ height: 250, p: 2, pt: 0 }}>
          {children}
        </Box>
      )}
    </Paper>
  );
};

export function Dashboard() {
    const { user: authUser, loading: authLoading } = useAuth();
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [profileLoading, setProfileLoading] = useState(true);
    const [metrics, setMetrics] = useState<DashboardMetrics>({
        totalSims: 0,
        activeSims: 0,
        inactiveSims: 0,
        totalClusters: 0,
    });
    // const [needsAttentionItems, setNeedsAttentionItems] = useState<any[]>([]);
    const [dailyUsageData, setDailyUsageData] = useState<UsageDataPoint[]>([]);
    const [monthlyUsageData, setMonthlyUsageData] = useState<UsageDataPoint[]>([]);
    const [businessHealthData, setBusinessHealthData] = useState<HistoricalCountDataPoint[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // State for date selection
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth()); // 0-indexed month

    // State for enhanced dashboard features
    const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set());
    const [thresholdData, setThresholdData] = useState<{
        threshold_7_days_count: number;
        threshold_7_days_estimated_cost: number;
        threshold_15_days_count: number;
        threshold_15_days_estimated_cost: number;
        threshold_30_days_count: number;
        threshold_30_days_estimated_cost: number;
        total_estimated_cost: number;
        monthly_management_fee: number;
        required_wallet_balance: number;
        current_wallet_balance: number;
        funding_gap: number;
    } | null>(null);

    // Memoize selected date string for display
    const selectedDateString = useMemo(() => {
        const monthName = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][selectedMonth];
        return `${monthName} ${selectedYear}`;
    }, [selectedMonth, selectedYear]);

    // Fetch user profile (role, org_id) from Supabase
    useEffect(() => {
        const fetchProfile = async () => {
            if (authUser && !authLoading) {
                setProfileLoading(true);
                try {
                    const { data, error } = await supabase
                        .from('profiles')
                        .select(`
                            id,
                            role,
                            organization_id,
                            organizations ( name )
                        `)
                        .eq('id', authUser.id)
                        .single();

                    if (error) throw error;

                    if (data) {
                        setProfile({
                            id: data.id,
                            role: data.role as UserProfile['role'],
                            organization_id: data.organization_id,
                            organization_name: (data.organizations as any)?.name // Extract org name
                        });
                        

                    } else {
                        setError('User profile not found.');
                    }
                } catch (err: any) {
                    console.error("Error fetching user profile:", err);
                    setError(err.message || 'Failed to load user profile.');
                    setProfile(null);
                } finally {
                    setProfileLoading(false);
                }
            } else if (!authLoading) {
                 // Handle case where authUser is null after auth loading finishes (e.g., not logged in)
                 setError('User not authenticated.');
                 setProfileLoading(false);
            }
        };
        fetchProfile();
    }, [authUser, authLoading]);

    // Fetch dashboard data based on user role and selected date
    const fetchDashboardData = useCallback(async (userProfile: UserProfile, year: number, month: number) => {
        if (!userProfile) return;
        setLoading(true);
        setError(null);

        const { role, organization_id: orgId } = userProfile;
        const isSuperAdmin = role === 'superadmin';

        try {
            // --- Calculate Date Ranges ---
            // For selected month (Daily Usage)
            const firstDaySelectedMonth = new Date(year, month, 1).toISOString();
            const firstDayNextMonth = new Date(year, month + 1, 1).toISOString();
            
            // For last 12 months (Monthly Usage & Business Health)
            const today = new Date(); // Use current date for rolling 12 months
            const firstDay12MonthsAgo = new Date(today.getFullYear() - 1, today.getMonth(), 1).toISOString();
            const firstDay13MonthsAgo = new Date(today.getFullYear() - 1, today.getMonth() -1, 1).toISOString();

            // Admin-specific: Fetch cluster IDs for their org
            let adminClusterIds: string[] | null = null;
            if (!isSuperAdmin && orgId) {
                const { data: clusterData, error: clusterError } = await supabase
                    .from('clusters')
                    .select('id')
                    .eq('organization_id', orgId);
                if (clusterError) throw clusterError;
                adminClusterIds = clusterData?.map(c => c.id) || [];
                // Handle case where admin has no clusters yet
                if (adminClusterIds.length === 0) {
                   console.warn("Admin belongs to an organization with no clusters.");
                   // We can still proceed, but usage/cluster queries will return empty.
                }
            }

            // --- Base Queries (adjust filtering based on role and date) ---
            let orgsQuery = supabase.from('organizations').select('id, name, wallet_balance, low_balance_threshold, management_fee_due');
            let simsQuery = supabase.from('sim_cards').select('id, status', { count: 'exact' });
            let feesDueQuery = supabase.from('organizations').select('id', { count: 'exact', head: true }).gt('management_fee_due', 0);
            let pendingReqsQuery = supabase.from('admin_requests').select('id', { count: 'exact', head: true }).eq('status', 'pending');
            let clustersQuery = supabase.from('clusters').select('id', { count: 'exact', head: true });
            
            // Daily Usage query uses selected month/year
            let dailyUsageQuery = supabase.from('usage_trends')
                .select('period_start, data_consumption')
                .eq('period_type', 'day')
                .gte('period_start', firstDaySelectedMonth)
                .lt('period_start', firstDayNextMonth) // Use less than next month start
                .order('period_start', { ascending: true });
            
            // Monthly Usage query uses rolling 12 months
            let monthlyUsageQuery = supabase.from('usage_trends')
                .select('period_start, data_consumption')
                .eq('period_type', 'month')
                .gte('period_start', firstDay12MonthsAgo)
                .order('period_start', { ascending: true });

            // Apply Admin filters (orgId, adminClusterIds)
            if (!isSuperAdmin && orgId) {
                orgsQuery = orgsQuery.eq('id', orgId);
                simsQuery = simsQuery.eq('organization_id', orgId); // Assumes sim_cards has organization_id
                feesDueQuery = feesDueQuery.eq('id', orgId);
                clustersQuery = clustersQuery.eq('organization_id', orgId);
                if (adminClusterIds && adminClusterIds.length > 0) {
                    dailyUsageQuery = dailyUsageQuery.in('cluster_id', adminClusterIds);
                    monthlyUsageQuery = monthlyUsageQuery.in('cluster_id', adminClusterIds);
                } else {
                    // Ensure usage queries return empty if no clusters
                    dailyUsageQuery = dailyUsageQuery.eq('cluster_id', '00000000-0000-0000-0000-000000000000'); 
                    monthlyUsageQuery = monthlyUsageQuery.eq('cluster_id', '00000000-0000-0000-0000-000000000000');
                }
            }

            // --- RPC Calls (pass orgId if admin, no date change needed here) ---
            const historicalCountsRpcParams = {
                 p_start_date: firstDay13MonthsAgo,
                 p_interval: 'month',
                 p_organization_id: isSuperAdmin ? null : orgId
             };

            // --- Execute Queries ---            
            const promises = [
                orgsQuery,
                simsQuery,
                feesDueQuery,
                isSuperAdmin ? pendingReqsQuery : Promise.resolve({ data: null, count: 0, error: null }),
                clustersQuery,
                dailyUsageQuery,
                monthlyUsageQuery,
                supabase.rpc('get_historical_counts', historicalCountsRpcParams),
                supabase.rpc('get_threshold_monitoring_summary', { p_organization_id: isSuperAdmin ? null : orgId })
            ];

            const [
                orgsRes, simsRes, feesDueRes, pendingReqsRes,
                clustersRes, dailyUsageRes, monthlyUsageRes,
                historicalCountsRes, thresholdRes
            ] = await Promise.all(promises);

            // --- Error Handling ---
             const errors = [
                 orgsRes?.error, simsRes?.error, feesDueRes?.error, pendingReqsRes?.error,
                 clustersRes?.error, dailyUsageRes?.error, monthlyUsageRes?.error,
                 historicalCountsRes?.error, thresholdRes?.error
             ].filter(Boolean);

            if (errors.length > 0) {
                console.error("Errors fetching dashboard data:", errors);
                throw new Error(`Failed to load dashboard data: ${errors.map(e => e?.message).join(', ')}`);
            }

            // --- Process Data & Calculate Metrics ---
            const orgsData: OrgDataForMetrics[] = (orgsRes as any)?.data ?? [];
            const simsData: SimCardStatus[] = (simsRes as any)?.data ?? [];
            const simsCount = (simsRes as any)?.count ?? 0;
            const feesDueCount = (feesDueRes as any)?.count ?? 0;
            const pendingReqsCount = (pendingReqsRes as any)?.count ?? 0;
            const clustersCount = (clustersRes as any)?.count ?? 0;

            // Metrics adapted for role
            let calculatedMetrics: DashboardMetrics;
            if (isSuperAdmin) {
                calculatedMetrics = {
                    totalOrganizations: orgsData.length,
                    totalClusters: clustersCount,
                    totalSims: simsCount,
                    activeSims: simsData.filter((sim: SimCardStatus) => sim.status === 'active').length,
                    inactiveSims: simsData.filter((sim: SimCardStatus) => sim.status === 'inactive').length,
                    totalWalletBalance: orgsData.reduce((sum: number, org: OrgDataForMetrics) => sum + (org.wallet_balance || 0), 0),
                    orgsWithLowBalanceCount: orgsData.filter((org: OrgDataForMetrics) => (org.wallet_balance || 0) < (org.low_balance_threshold || 0)).length,
                    orgsWithFeesDueCount: feesDueCount,
                    pendingRequestCount: pendingReqsCount,
                };
            } else if (orgsData.length === 1) { // Admin should only have one org
                const myOrg = orgsData[0];
                calculatedMetrics = {
                    organizationName: myOrg.name,
                    totalClusters: clustersCount,
                    totalSims: simsCount,
                    activeSims: simsData.filter((sim: SimCardStatus) => sim.status === 'active').length,
                    inactiveSims: simsData.filter((sim: SimCardStatus) => sim.status === 'inactive').length,
                    walletBalance: myOrg.wallet_balance || 0,
                    isLowBalance: (myOrg.wallet_balance || 0) < (myOrg.low_balance_threshold || 0),
                    hasFeesDue: myOrg.management_fee_due > 0,
                };
            } else {
                 // Handle unexpected state (e.g., admin with 0 or >1 orgs assigned?)
                 console.warn("Admin user associated with an unexpected number of organizations:", orgsData.length);
                 // Set default/empty metrics
                 calculatedMetrics = {
                     totalSims: simsCount,
                     activeSims: simsData.filter((sim: SimCardStatus) => sim.status === 'active').length,
                     inactiveSims: simsData.filter((sim: SimCardStatus) => sim.status === 'inactive').length,
                     totalClusters: clustersCount,
                 };
            }
            setMetrics(calculatedMetrics);

            // Needs Attention Items (role-based)
            let attentionItems = [];
            if (isSuperAdmin) {
                if ((calculatedMetrics.orgsWithLowBalanceCount ?? 0) > 0) attentionItems.push({ icon: AccountBalanceWalletIcon, text: `${calculatedMetrics.orgsWithLowBalanceCount} Organizations have low balance`, severity: 'warning' });
                if ((calculatedMetrics.orgsWithFeesDueCount ?? 0) > 0) attentionItems.push({ icon: MoneyOffIcon, text: `${calculatedMetrics.orgsWithFeesDueCount} Organizations have management fees due`, severity: 'error' });
                if ((calculatedMetrics.pendingRequestCount ?? 0) > 0) attentionItems.push({ icon: PendingActionsIcon, text: `${calculatedMetrics.pendingRequestCount} Pending Admin Requests`, severity: 'info' });
            } else {
                if (calculatedMetrics.isLowBalance) attentionItems.push({ icon: AccountBalanceWalletIcon, text: `Your organization's balance is low`, severity: 'warning' });
                if (calculatedMetrics.hasFeesDue) attentionItems.push({ icon: MoneyOffIcon, text: `Management fees are due for your organization`, severity: 'error' });
            }
             // Common attention items (if any)
            // attentionItems.push({ icon: SimCardIcon, text: `X SIMs nearing data limit`, severity: 'warning' });
            // setNeedsAttentionItems(attentionItems);

            // --- Process Chart/Table Data ---
            setDailyUsageData(((dailyUsageRes as any)?.data ?? []).map((d: any) => ({ name: formatAxisDate(d.period_start), usage: d.data_consumption })) || []);
            setMonthlyUsageData(((monthlyUsageRes as any)?.data ?? []).map((d: any) => ({ name: formatAxisMonth(d.period_start), usage: d.data_consumption })) || []);
            setBusinessHealthData(((historicalCountsRes as any)?.data as HistoricalCountDataPoint[]) ?? []);
            
            // --- Process Threshold Data ---
            const thresholdSummary = (thresholdRes as any)?.data?.[0];
            if (thresholdSummary) {
                setThresholdData({
                    threshold_7_days_count: thresholdSummary.threshold_7_days_count || 0,
                    threshold_7_days_estimated_cost: thresholdSummary.threshold_7_days_estimated_cost || 0,
                    threshold_15_days_count: thresholdSummary.threshold_15_days_count || 0,
                    threshold_15_days_estimated_cost: thresholdSummary.threshold_15_days_estimated_cost || 0,
                    threshold_30_days_count: thresholdSummary.threshold_30_days_count || 0,
                    threshold_30_days_estimated_cost: thresholdSummary.threshold_30_days_estimated_cost || 0,
                    total_estimated_cost: thresholdSummary.total_estimated_cost || 0,
                    monthly_management_fee: thresholdSummary.monthly_management_fee || 0,
                    required_wallet_balance: thresholdSummary.required_wallet_balance || 0,
                    current_wallet_balance: thresholdSummary.current_wallet_balance || 0,
                    funding_gap: thresholdSummary.funding_gap || 0,
                });
            } else {
                setThresholdData(null);
            }

        } catch (err: any) {
            console.error("Error fetching dashboard data:", err);
            setError(err.message || 'Failed to load dashboard data.');
            setMetrics({
                 totalSims: 0,
                 activeSims: 0,
                 inactiveSims: 0,
                 totalClusters: 0,
             });
            // setNeedsAttentionItems([]);
            setDailyUsageData([]); setMonthlyUsageData([]); setBusinessHealthData([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Trigger data fetch when profile is loaded or date changes
    useEffect(() => {
        if (profile) {
            fetchDashboardData(profile, selectedYear, selectedMonth);
        }
    }, [profile, selectedYear, selectedMonth, fetchDashboardData]);

    // Handle loading states
    if (authLoading || profileLoading) {
        return <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Container>;
    }

    // Handle errors
    if (error) {
        return <Container sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Container>;
    }
    
    // Handle case where profile failed to load but wasn't caught by error state
    if (!profile) {
        return <Container sx={{ mt: 4 }}><Alert severity="error">Could not load user profile. Please try again.</Alert></Container>;
    }

    // Determine role for UI rendering
    const isSuperAdmin = profile.role === 'superadmin';

    // Helper functions for alerts and actions
    const handleDismissAlert = (alertId: string) => {
        setDismissedAlerts(prev => new Set([...Array.from(prev), alertId]));
    };

    const handleAddFunds = () => {
        // Navigate to wallet page or open add funds modal
        window.location.href = '/wallet';
    };

    const urgentSims = thresholdData?.threshold_7_days_count || 0;
    const actionRequiredSims = thresholdData?.threshold_15_days_count || 0;
    const totalAtRisk = urgentSims + actionRequiredSims;
    const fundingGap = thresholdData?.funding_gap || 0;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* --- Alert Banners --- */}
            {!loading && thresholdData && !isSuperAdmin && (
                <>
                    {urgentSims > 0 && !dismissedAlerts.has('urgent-sims') && (
                        <AlertBanner
                            severity="error"
                            title="URGENT: SIMs Need Immediate Attention"
                            message={`${urgentSims} SIM${urgentSims > 1 ? 's' : ''} will run out of data within 1-7 days. Estimated cost: ${formatCurrency(thresholdData.threshold_7_days_estimated_cost)}`}
                            action={
                                <Button 
                                    color="inherit" 
                                    size="small" 
                                    component={RouterLink} 
                                    to="/sims"
                                >
                                    View SIMs
                                </Button>
                            }
                            onDismiss={() => handleDismissAlert('urgent-sims')}
                        />
                    )}
                    
                    {fundingGap > 0 && !dismissedAlerts.has('funding-gap') && (
                        <AlertBanner
                            severity="warning"
                            title="Funding Gap Detected"
                            message={`Add ${formatCurrency(fundingGap)} to your wallet to cover estimated top-up costs and management fees.`}
                            action={
                                <Button 
                                    color="inherit" 
                                    size="small" 
                                    onClick={handleAddFunds}
                                >
                                    Add Funds
                                </Button>
                            }
                            onDismiss={() => handleDismissAlert('funding-gap')}
                        />
                    )}
                </>
            )}

            {/* --- Key Metrics Row --- */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {isSuperAdmin ? (
                    <>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <EnhancedStatCard 
                                title="Total Organizations" 
                                value={metrics.totalOrganizations ?? '-'} 
                                icon={<BusinessIcon />}
                                color='primary'
                                subtitle="Active clients"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <EnhancedStatCard 
                                title="SIMs at Risk" 
                                value={totalAtRisk} 
                                icon={<WarningIcon />}
                                color={totalAtRisk > 0 ? 'error.main' : 'success.main'}
                                subtitle="Need attention"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <EnhancedStatCard 
                                title="Total SIMs" 
                                value={`${metrics.activeSims}/${metrics.totalSims}`} 
                                icon={<SimCardIcon />}
                                color='primary'
                                subtitle="Active/Total"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <EnhancedStatCard 
                                title="Total Wallet Balance" 
                                value={formatCurrency(metrics.totalWalletBalance ?? 0)} 
                                icon={<AccountBalanceWalletIcon />}
                                color='primary'
                                subtitle="Across all organizations"
                            />
                        </Grid>
                    </>
                ) : (
                    <>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <EnhancedStatCard 
                                title="Organization" 
                                value={metrics.organizationName ?? '-'} 
                                icon={<BusinessIcon />}
                                color='primary'
                                isLoading={profileLoading}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <EnhancedStatCard 
                                title="SIMs at Risk" 
                                value={totalAtRisk} 
                                icon={<WarningIcon />}
                                color={totalAtRisk > 0 ? 'error.main' : 'success.main'}
                                subtitle={totalAtRisk > 0 ? "Need attention" : "All good"}
                                onClick={totalAtRisk > 0 ? () => window.location.href = '/sims' : undefined}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <EnhancedStatCard 
                                title="Total Clusters" 
                                value={metrics.totalClusters ?? '-'} 
                                icon={<GroupWorkIcon />}
                                color='primary'
                                onClick={() => window.location.href = '/clusters'}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <EnhancedStatCard 
                                title="Wallet Balance" 
                                value={formatCurrency(metrics.walletBalance ?? 0)} 
                                icon={<AccountBalanceWalletIcon />}
                                color={metrics.isLowBalance ? 'warning.main' : 'success.main'}
                                subtitle={metrics.isLowBalance ? "Low balance" : "Sufficient"}
                                onClick={handleAddFunds}
                            />
                        </Grid>
                    </>
                )}
            </Grid>

            {/* --- Threshold Monitoring (Enhanced and Prominent) --- */}
            {!isSuperAdmin && thresholdData && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, md: 8 }}>
                        <ThresholdMonitoringDashboard 
                            organizationId={profile.organization_id}
                            onRefresh={() => profile && fetchDashboardData(profile, selectedYear, selectedMonth)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <FinancialSummaryCard
                            walletBalance={metrics.walletBalance ?? 0}
                            estimatedCost={thresholdData.total_estimated_cost}
                            monthlyFee={thresholdData.monthly_management_fee}
                            requiredBalance={thresholdData.required_wallet_balance}
                            fundingGap={fundingGap}
                            isLowBalance={metrics.isLowBalance ?? false}
                            onAddFunds={handleAddFunds}
                        />
                    </Grid>
                </Grid>
            )}

            {/* --- Superadmin Threshold Monitoring --- */}
            {isSuperAdmin && (
                <Box sx={{ mb: 3 }}>
                    <ThresholdMonitoringDashboard 
                        organizationId={null}
                        onRefresh={() => profile && fetchDashboardData(profile, selectedYear, selectedMonth)}
                    />
                </Box>
            )}

            {/* --- Top Bar: Refresh & Date Selection --- */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                     <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="year-select-label">Year</InputLabel>
                        <Select
                            labelId="year-select-label"
                            id="year-select"
                            value={selectedYear}
                            label="Year"
                            onChange={(e) => setSelectedYear(e.target.value as number)}
                            disabled={loading}
                        >
                            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                     <FormControl size="small" sx={{ minWidth: 150 }}>
                         <InputLabel id="month-select-label">Month</InputLabel>
                        <Select
                            labelId="month-select-label"
                            id="month-select"
                            value={selectedMonth}
                            label="Month"
                            onChange={(e) => setSelectedMonth(e.target.value as number)}
                            disabled={loading}
                        >
                            {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, index) => (
                                <MenuItem key={index} value={index}>{month}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => profile && fetchDashboardData(profile, selectedYear, selectedMonth)}
                    disabled={loading || !profile}
                >
                    Refresh Data
                </Button>
            </Box>
            
            {/* --- Usage Trends (Compact) --- */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, lg: 6 }}>
                    <CompactChartContainer 
                        title={`Daily Usage (${selectedDateString})`}
                        isCollapsible={true}
                        defaultExpanded={true}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={dailyUsageData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" fontSize={10} tickFormatter={formatAxisDate} interval="preserveStartEnd" />
                                <YAxis fontSize={10} unit=" MB" allowDecimals={false}/>
                                <Tooltip formatter={(value: number) => [`${value.toFixed(1)} MB`, 'Usage']} />
                                <Bar dataKey="usage" fill="#8884d8" barSize={10} />
                            </BarChart>
                        </ResponsiveContainer>
                    </CompactChartContainer>
                </Grid>
                
                <Grid size={{ xs: 12, lg: 6 }}>
                    <CompactChartContainer 
                        title="Monthly Usage (Last 12 Months)"
                        isCollapsible={true}
                        defaultExpanded={true}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={monthlyUsageData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" fontSize={10} tickFormatter={formatAxisMonth} interval="preserveStartEnd" />
                                <YAxis fontSize={10} unit=" MB" allowDecimals={false} />
                                <Tooltip formatter={(value: number) => [formatDataUsage(value), 'Usage']} />
                                <Bar dataKey="usage" fill="#82ca9d" />
                            </BarChart>
                        </ResponsiveContainer>
                    </CompactChartContainer>
                </Grid>
            </Grid>

            {/* --- Additional Charts for Superadmin --- */}
            {isSuperAdmin && (
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, lg: 8 }}>
                        <CompactChartContainer title="Business Health Trends (Last 12 Months)">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={businessHealthData} margin={{ top: 5, right: 30, left: -20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="date" fontSize={10} tickFormatter={formatAxisMonth} interval="preserveStartEnd" />
                                    <YAxis fontSize={10} allowDecimals={false} />
                                    <Tooltip formatter={(value: number, name: string) => [value, name]} />
                                    <Legend />
                                    <Line type="monotone" dataKey="orgs" stroke="#8884d8" strokeWidth={2} name="Organizations" />
                                    <Line type="monotone" dataKey="clusters" stroke="#82ca9d" strokeWidth={2} name="Clusters" />
                                    <Line type="monotone" dataKey="activeSims" stroke="#ffc658" strokeWidth={2} name="Active SIMs" />
                                </LineChart>
                            </ResponsiveContainer>
                        </CompactChartContainer>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 4 }}>
                        <CompactChartContainer title="SIM Status Distribution">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={[{ name: 'Active', value: metrics.activeSims ?? 0 }, { name: 'Inactive', value: metrics.inactiveSims ?? 0 }]}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={40}
                                        outerRadius={80}
                                        paddingAngle={2}
                                        dataKey="value"
                                    >
                                        {[{ name: 'Active', value: metrics.activeSims ?? 0 }, { name: 'Inactive', value: metrics.inactiveSims ?? 0 }].map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={['#4CAF50', '#F44336'][index]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </CompactChartContainer>
                    </Grid>
                </Grid>
            )}
    </Container>
  );
} 

// --- Helper: Stat Card Component (Updated) ---
// const StatCard = ({ title, value, icon, color, isLoading }: { title: string, value: string | number, icon: ReactElement, color?: string, isLoading?: boolean }) => (
//     <Card sx={{ height: '100%' }}>
//         <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
//             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
//                 <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
//                 <Box sx={{ fontSize: 30, color: 'text.secondary' }}>
//                     {icon}
//                 </Box>
//             </Box>
//             <Box sx={{ mt: 1 }}>
//                 {isLoading ? <CircularProgress size={24} /> : <Typography variant="h5" sx={{ color: color }}>{value}</Typography>}
//             </Box>
//         </CardContent>
//     </Card>
// );

// --- Placeholder Components (Added) ---
const PlaceholderBase = ({ title }: { title: string }) => (
    <Paper sx={{ p: 2, height: 300, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <CircularProgress />
    </Paper>
);
// const ChartPlaceholder = PlaceholderBase;
// const TablePlaceholder = PlaceholderBase;

// --- Placeholder RPC Function Definition for get_top_org_consumers (Updated) ---
/*
-- Create or Replace this function in your Supabase SQL Editor.
-- Accepts an optional organization_id to filter results for admins.

CREATE OR REPLACE FUNCTION get_top_org_consumers(
    p_start_time TIMESTAMPTZ,
    p_end_time TIMESTAMPTZ,
    p_organization_id UUID DEFAULT NULL -- Added parameter
)
RETURNS TABLE(id UUID, name TEXT, usage NUMERIC)
AS $$
BEGIN
    RETURN QUERY
    SELECT
        o.id,
        o.name,
        SUM(ut.data_consumption) AS total_usage
    FROM
        organizations o
    JOIN
        clusters c ON o.id = c.organization_id
    JOIN
        usage_trends ut ON c.id = ut.cluster_id
    WHERE
        ut.period_type = 'month' -- Match query period type
        AND ut.period_start >= p_start_time
        AND ut.period_start < p_end_time
        AND (p_organization_id IS NULL OR o.id = p_organization_id) -- Filter by org ID if provided
    GROUP BY
        o.id, o.name
    ORDER BY
        total_usage DESC NULLS LAST -- Ensure nulls are last
    LIMIT 10;
END;
$$ LANGUAGE plpgsql;
*/

// --- REQUIRED RPC Function Definition for Business Health Chart (Updated) ---
/*
-- Create or Replace this function in your Supabase SQL Editor
-- Accepts an optional organization_id to filter results for admins.

CREATE OR REPLACE FUNCTION get_historical_counts(
    p_start_date TIMESTAMPTZ,
    p_interval TEXT, -- e.g., 'month', 'week', 'day'
    p_organization_id UUID DEFAULT NULL -- Added parameter
)
RETURNS TABLE(
    date TEXT,
    orgs BIGINT,
    clusters BIGINT,
    "activeSims" BIGINT
)
AS $$
BEGIN
    RETURN QUERY
    WITH date_series AS (
        SELECT date_trunc(p_interval, dd)::date AS interval_start
        FROM generate_series(
                 p_start_date,
                 NOW(),
                 ('1 ' || p_interval)::interval
             ) AS dd
        GROUP BY interval_start
    ),
    org_counts AS (
        SELECT
            date_trunc(p_interval, created_at)::date AS interval_start,
            COUNT(*) AS count
        FROM organizations
        WHERE created_at >= p_start_date
          AND (p_organization_id IS NULL OR id = p_organization_id) -- Filter
        GROUP BY interval_start
    ),
    cluster_counts AS (
        SELECT
            date_trunc(p_interval, created_at)::date AS interval_start,
            COUNT(*) AS count
        FROM clusters
         WHERE created_at >= p_start_date
           AND (p_organization_id IS NULL OR organization_id = p_organization_id) -- Filter
        GROUP BY interval_start
    )
    SELECT
        ds.interval_start::TEXT AS date,
        (SUM(COALESCE(oc.count, 0)) OVER (ORDER BY ds.interval_start ASC ROWS BETWEEN UNBOUNDED PRECEDING AND CURRENT ROW))::BIGINT AS orgs,
        (SUM(COALESCE(cc.count, 0)) OVER (ORDER BY ds.interval_start ASC ROWS BETWEEN UNBOUNDED PRECEDING AND CURRENT ROW))::BIGINT AS clusters,
        (SELECT COUNT(*)
         FROM sim_cards sc
         WHERE sc.status = 'active'
           AND sc.created_at <= ds.interval_start + ('1 ' || p_interval)::interval - interval '1 second'
           AND (p_organization_id IS NULL OR sc.organization_id = p_organization_id) -- Filter
        ) AS "activeSims"
    FROM date_series ds
    LEFT JOIN org_counts oc ON ds.interval_start = oc.interval_start
    LEFT JOIN cluster_counts cc ON ds.interval_start = cc.interval_start
    ORDER BY ds.interval_start;
END;
$$ LANGUAGE plpgsql;

*/ 